import { createRouter, createWebHistory } from 'vue-router';
import { moduleExampleRoutes } from './modules/module-example/routes';
import { dashboardModuleRoutes } from './modules/dashboard/routes';
import { whatsappRoutes } from './modules/whatsapp/routes';
import { useLayoutStore } from './store/shared/layout-store';
import { useAuthStore } from './store/shared/auth-store';
import { reportsModuleRoutes } from './modules/reports/routes';
import { ordersModuleRoutes } from './modules/orders/routes';
import { integrationsModuleRoutes } from './modules/integrations/routes';
import { settingsModuleRoutes } from './modules/settings/routes';
import { productsModuleRoutes } from './modules/products/routes';
import { pdfGeneratorModuleRoutes } from './modules/pdf-generator/routes';
import { invoicesModuleRoutes } from './modules/invoices/routes';
import { contactsRoutes } from './modules/contacts/routes';
import { isFeatureFlagEnabled } from '@/v2/entities/feature-flag';
import { importsRoutes } from './modules/imports/routes';
import { kodoratAgentRoutes } from './agents/kodorat/routes';
import { featureFlag } from './entities/feature-flag';
import { ziebartAgentRoutes } from './agents/ziebart/routes';
import { authRoutes } from './modules/auth/routes';
import { coreModuleRoutes } from './modules/core/routes';
import { hdtcAgentRoutes } from './agents/hdtc/routes';
import { realEstateAgentRoutes } from './agents/real-estate/routes';
import { sharedAgentRoutes } from './agents/shared/routes';
import { agentEcommerceRoutes } from './modules/agent-ecommerce/routes';
import { socialMentionsRoutes } from './modules/social-mentions/routes';
import { verifySyRoutes } from './agents/verify-sy/routes';
import { linkerAgentRoutes } from './agents/linker/routes';
import { sadidleeRoutes } from './agents/sadidlee/routes';
import AuthorizeService from './services/authorize';
import { subscriptionRoutes } from './modules/subscription/routes';
import { paymentTestRoutes } from './modules/payment-test/routes';

const routes = [
	{
		path: '/v2',
		name: 'v2-root',
		component: () => import('./layouts/main-layout.vue'),
		children: [
			moduleExampleRoutes,
			dashboardModuleRoutes,
			reportsModuleRoutes,
			whatsappRoutes,
			integrationsModuleRoutes,
			settingsModuleRoutes,
			ordersModuleRoutes,
			productsModuleRoutes,
			invoicesModuleRoutes,
			contactsRoutes,
			importsRoutes,
			kodoratAgentRoutes,
			ziebartAgentRoutes,
			coreModuleRoutes,
			hdtcAgentRoutes,
			realEstateAgentRoutes,
			sharedAgentRoutes,
			agentEcommerceRoutes,
			socialMentionsRoutes,
			verifySyRoutes,
			linkerAgentRoutes,
			paymentTestRoutes,
			sadidleeRoutes,
		],
	},
	{
		path: '/v2',
		component: () => import('./layouts/auth-layout.vue'),
		children: [authRoutes, subscriptionRoutes],
	},
	pdfGeneratorModuleRoutes,
	{
		path: '/:catchAll(.*)*',
		component: () => import('./components/page-not-found.vue'),
	},
];

const router = createRouter({
	history: createWebHistory(),
	routes,
});

const publicRoutes = ['v2/auth', 'v2/subscription'];

const isPublicRoute = (route) => {
	return publicRoutes.some((publicRoute) => route.includes(publicRoute));
};

router.beforeEach(async (to, from, next) => {
	const layoutStore = useLayoutStore();
	const authStore = useAuthStore();

	if (to.meta?.title) {
		if (typeof to.meta.title === 'string') {
			layoutStore.setPageTitle(to.meta.title);
			document.title = to.meta?.title;
		} else if (typeof to.meta.title == 'function') {
			const title = to.meta.title({ route: to });
			layoutStore.setPageTitle(title);
			document.title = title;
		} else {
			console.error('[Vue Router] unsupported type of title in route meta');
		}
	} else {
		layoutStore.setPageTitle('');
		document.title = 'linker-marketplace';
	}
	console.log({ to, from });

	if (!to.fullPath?.includes('v2/pdf-generator')) {
		if (isPublicRoute(to.fullPath)) {
			// Check for incomplete registration even on public routes (after auth)
			if (authStore.isLoggedIn && !to.fullPath.includes('v2/subscription')) {
				const resumeInfo = await authStore.checkAndResumeRegistration();
				if (resumeInfo?.shouldResume) {
					next(resumeInfo.resumeUrl);
					return;
				}
			}
			return next();
		}

		const forceFetchUser = from.fullPath === '/';
		await authStore.fetchUser(forceFetchUser);

		// Handle /v2 root redirect after feature flags are loaded
		if (to.name === 'v2-root') {
			if (isFeatureFlagEnabled([featureFlag.E_COMMERCE, featureFlag.AI_AGENT])) {
				next('/v2/dashboard/main');
			} else {
				next('/v2/whatsapp');
			}
			return;
		}

		// Check for incomplete registration after authentication
		if (authStore.isLoggedIn && !to.fullPath.includes('v2/subscription')) {
			const resumeInfo = await authStore.checkAndResumeRegistration();
			if (resumeInfo?.shouldResume) {
				next(resumeInfo.resumeUrl);
				return;
			}
		}

		if (to.meta.featureFlag) {
			if (!isFeatureFlagEnabled(to.meta.featureFlag)) {
				next('/v2');
				return;
			}
		}

		if (to.meta?.can) {
			const can = await to.meta?.can();
			if (can === true) {
				next();
				return;
			} else if (typeof can === 'string') {
				next(can);
			} else {
				next('/v2');
			}
			return;
		}

		if (to.meta?.permissions) {
			const permissions = Array.isArray(to.meta.permissions)
				? to.meta.permissions
				: [to.meta.permissions];
			const hasPermission = AuthorizeService.hasAnyPermission(permissions);

			if (!hasPermission) {
				next('/v2');
				return;
			}
		}

		next();
	} else {
		const token = to.query['token'];
		if (!token) {
			next(new Error('token not provided in query params'));
			return;
		}
	}
});

export { router };
